// Ferramentas para ler o texto de cobertura médica que vem de cada seguradora.
// Os formatos variam muito, por exemplo:
//   Universal:   "US$ 80.000"
//   Affinity:    "EUR30.000,00 / US$30.000,00"   ou   "US$160.000,000"
//   Assist Card: "EUROPA EUR 60.000/ RESTO DO MUNDO USD 60.000 / DMH COVID-19 EUR/USD 30.000"
// Sempre usamos o PRIMEIRO valor do texto, que é o valor principal do plano.

// "$" sozinho (Universal) conta como dólar; "R$" (Affinity, em alguns itens) é real.
const AMOUNT_REGEX = /(R\$|EUR|USD|US\$|€|\$)\s*(\d{1,3}(?:\.\d{3})+|\d+)(?:,\d+)?/gi;

const normalizeCurrency = (raw) => {
  const upper = raw.toUpperCase();
  if (upper === 'R$') return 'BRL';
  return upper === 'EUR' || raw === '€' ? 'EUR' : 'USD';
};

// Todos os valores encontrados no texto: [{ currency: 'EUR', amount: 60000 }, ...]
export const findAmounts = (text, minAmount = 1000) => {
  const amounts = [];
  for (const match of String(text || '').matchAll(AMOUNT_REGEX)) {
    const amount = parseInt(match[2].replace(/\./g, ''), 10);
    if (amount >= minAmount) {
      amounts.push({ currency: normalizeCurrency(match[1]), amount });
    }
  }
  return amounts;
};

// Valor principal da cobertura, ou null se não der para ler.
// A Assist Card manda os dois valores ("EUROPA EUR 60.000/ RESTO DO MUNDO USD 60.000"):
// na Europa mostramos o valor em euro; nos outros destinos, o valor em dólar.
export const pickAmount = (amounts, isEurope) =>
  amounts.find((a) => a.currency === (isEurope ? 'EUR' : 'USD')) ?? amounts[0] ?? null;

export const parseMedicalCoverage = (text, isEurope = false) =>
  pickAmount(findAmounts(text), isEurope);

// Número puro (para ordenar e filtrar). Euro e dólar contam igual aqui.
export const medicalCoverageValue = (text) =>
  parseMedicalCoverage(text)?.amount ?? 0;

// Bagagem tem valores menores (ex: "EUR800,00"), então aceita qualquer valor.
export const luggageCoverageValue = (text) => findAmounts(text, 1)[0]?.amount ?? 0;

// Texto da cobertura de bagagem do plano. O campo luggageCoverage às vezes vem
// "Sem cobertura" mesmo quando o plano cobre: a Assist Card chama a cobertura de
// "Extravio de Bagagem Plus" (e o servidor só reconhece "Seguro Bagagem Plus").
// Nesses casos, buscamos o valor na lista completa de coberturas.
const LUGGAGE_ITEM_REGEX = /(extravio|perda) de bagagem|seguro bagagem/i;

export const getLuggageText = (insurance) => {
  const field = insurance?.luggageCoverage;
  if (luggageCoverageValue(field)) return field;
  const item = (insurance?.coverageList || []).find(
    (c) => LUGGAGE_ITEM_REGEX.test(c.description) && luggageCoverageValue(c.value)
  );
  return item ? item.value : field;
};

const CURRENCY_SYMBOL = { EUR: '€', USD: 'US$', BRL: 'R$' };

export const formatAmount = ({ currency, amount }) =>
  `${CURRENCY_SYMBOL[currency]} ${amount.toLocaleString('pt-BR')}`;

// Texto curto para exibir: "€ 60.000" / "US$ 80.000".
export const formatMedicalCoverage = (text, isEurope = false) => {
  const parsed = parseMedicalCoverage(text, isEurope);
  return parsed ? formatAmount(parsed) : text;
};

// Mesmo formato para bagagem (ex: "EUROPA EUR 1.200 (EUR 80/ kg)/..." -> "€ 1.200").
export const formatLuggageCoverage = (text, isEurope = false) => {
  const parsed = pickAmount(findAmounts(text, 1), isEurope);
  return parsed ? formatAmount(parsed) : text;
};

// Exigência do Espaço Schengen (Europa): cobertura médica mínima de €30.000.
// Planos em dólar só contam a partir de US$ 40.000, para ter folga no câmbio.
export const SCHENGEN_MIN_EUR = 30000;
const SCHENGEN_MIN_USD = 40000;

export const meetsSchengen = (text) =>
  findAmounts(text).some(({ currency, amount }) =>
    currency === 'EUR'
      ? amount >= SCHENGEN_MIN_EUR
      : currency === 'USD' && amount >= SCHENGEN_MIN_USD
  );

// O plano cobre Covid-19? O campo isCovidCovered vem errado da Affinity (o
// servidor só marca quando o NOME do plano tem "covid"), mas a lista de
// coberturas dela traz "Despesas Médicas e Hospitalares por COVID-19: Sim
// incluso no DMHO". Por isso também olhamos a lista de coberturas.
export const coversCovid = (insurance) => {
  if (insurance?.isCovidCovered) return true;
  return (insurance?.coverageList || []).some((item) => {
    if (!/covid/i.test(item.description)) return false;
    const value = String(item.value || '').trim().toLowerCase();
    return /^(sim|incluido|incluso)/.test(value) || findAmounts(value, 1).length > 0;
  });
};

// Planos que só podem ser vendidos para estudantes (ex: "Affinity 30 Estudante").
export const isStudentOnly = (insurance) =>
  /estudante/i.test(`${insurance?.insuranceName ?? ''} ${insurance?.ageInfo ?? ''}`);

// Preço total do grupo para um plano.
export const getTotalPrice = (insurance, youngPassengers, oldPassengers) =>
  (insurance?.passengerPrice ?? 0) * youngPassengers +
  (insurance?.oldPassengerPrice ?? 0) * oldPassengers;

export const formatBRL = (value) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
